'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { Paragraph } from '../ui/paragraph';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { calculateReadingTime } from '../../utils/calculate-reading-time';
import { getTextFromHTML } from '../../utils/misc-utils';

export function FirstBlog({ blog, locale }: { blog: Blog; locale: string }) {
  const { t } = useTranslations();

  let formattedDate;

  try {
    formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  const textContent = getTextFromHTML(blog.content);

  return (
    <div
      className={cn(
        'custom-container',
        'mx-auto grid w-full grid-cols-1 overflow-hidden rounded-[4px] bg-[#FAF6E5] sm:grid-cols-2',
      )}
    >
      <div className="col-span-1 flex w-full flex-col items-start justify-between gap-6 p-6 lg:p-8">
        <div className="flex w-full flex-col items-start justify-start gap-4">
          <div className="flex w-full items-center justify-start gap-4">
            {blog.categories?.map(category => (
              <p
                key={category.name}
                className="flex h-[30px] items-center rounded-[4px] bg-white px-3 text-xs font-semibold uppercase text-black"
              >
                {category.name}
              </p>
            ))}
            <div className="flex items-center justify-center gap-2">
              <Paragraph size="sm">{formattedDate}</Paragraph>
              <Paragraph size="sm">|</Paragraph>
              <Paragraph size="sm">{calculateReadingTime(textContent)} min.</Paragraph>
            </div>
          </div>
          <Heading title={blog.name} className={{ title: 'line-clamp-3 uppercase' }} tag="h3" />
        </div>
        <Button asChild>
          <Link prefetch={false} href={ROUTES.BLOG_DETAIL({ slug: blog?.slug })}>
            {t('text3391')}
          </Link>
        </Button>
      </div>
      <Image
        src={blog?.image?.url}
        alt={resolveImageAlt(blog?.image, blog?.name)}
        width={0}
        height={0}
        className="col-span-1 h-[268px] w-full object-cover object-center sm:h-[440px] lg:h-[516px]"
        sizes="100vw"
        priority
        loading="eager"
      />
    </div>
  );
}
